import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Combobox } from "@/components/ui/combobox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Task,
  TaskPriority,
  RecurrencePattern,
  RecurrenceCalculationType,
  CreateTaskRequest,
  UpdateTaskRequest,
  useCreateTask,
  useUpdateTask,
} from "@/lib/api/checklist";
import { toast } from "@/components/ui/use-toast";

interface CreateEditTaskModalProps {
  open: boolean;
  onClose: () => void;
  task?: Task;
}

const priorityOptions = [
  { value: TaskPriority.LOW, label: "Low" },
  { value: TaskPriority.MEDIUM, label: "Medium" },
  { value: TaskPriority.HIGH, label: "High" },
  { value: TaskPriority.CRITICAL, label: "Critical" },
];

const recurrenceOptions = [
  { value: "", label: "Not Recurring" },
  { value: RecurrencePattern.DAILY, label: "Daily" },
  { value: RecurrencePattern.WEEKLY, label: "Weekly" },
  { value: RecurrencePattern.MONTHLY, label: "Monthly" },
  { value: RecurrencePattern.QUARTERLY, label: "Quarterly" },
  { value: RecurrencePattern.BIANNUALLY, label: "Biannually" },
  { value: RecurrencePattern.ANNUALLY, label: "Annually" },
];

const calculationTypeOptions = [
  { value: RecurrenceCalculationType.FIXED_SCHEDULE, label: "Fixed Schedule" },
  { value: RecurrenceCalculationType.FROM_COMPLETION, label: "From Completion" },
];

export function CreateEditTaskModal({
  open,
  onClose,
  task,
}: CreateEditTaskModalProps) {
  const isEdit = !!task;
  const createTask = useCreateTask();
  const updateTask = useUpdateTask(task?.id || "");

  const form = useForm<CreateTaskRequest | UpdateTaskRequest>({
    resolver: zodResolver(isEdit ? UpdateTaskRequest : CreateTaskRequest),
    defaultValues: {
      title: "",
      description: "",
      note: "",
      priority: TaskPriority.MEDIUM,
      recurrence_pattern: RecurrencePattern.ANNUALLY,
      recurrence_interval: 1,
      recurrence_calculation_type: RecurrenceCalculationType.FIXED_SCHEDULE,
      is_active: true,
    },
  });

  useEffect(() => {
    if (task) {
      form.reset({
        title: task.title,
        description: task.description || "",
        note: task.note || "",
        priority: task.priority,
        recurrence_pattern: task.recurrence_pattern,
        recurrence_interval: task.recurrence_interval || 1,
        recurrence_calculation_type:
          task.recurrence_calculation_type ||
          RecurrenceCalculationType.FIXED_SCHEDULE,
        is_active: task.is_active,
      });
    } else {
      form.reset({
        title: "",
        description: "",
        note: "",
        priority: TaskPriority.MEDIUM,
        recurrence_pattern: undefined,
        recurrence_interval: 1,
        recurrence_calculation_type: RecurrenceCalculationType.FIXED_SCHEDULE,
        is_active: true,
      });
    }
  }, [task, form]);

  const onSubmit = async (data: CreateTaskRequest | UpdateTaskRequest) => {
    try {
      if (isEdit) {
        const result = await updateTask.mutateAsync(data as UpdateTaskRequest);
        if (!result?.success) {
          throw new Error(result?.message || "Failed to update task");
        }
        toast({
          title: "Task updated",
          description: "The task has been updated successfully.",
        });
      } else {
        const result = await createTask.mutateAsync(data as CreateTaskRequest);
        if (!result?.success) {
          throw new Error(result?.message || "Failed to create task");
        }
        toast({
          title: "Task created",
          description: "The task has been created successfully.",
        });
      }
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || `Failed to ${isEdit ? "update" : "create"} task.`,
        variant: "destructive",
      });
    }
  };

  const showRecurrence = !!form.watch("recurrence_pattern");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] md:max-w-[800px] lg:max-w-[1000px]">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">{isEdit ? "Edit Task" : "Create New Task"}</DialogTitle>
          <DialogDescription className="text-base">
            {isEdit
              ? "Update the task details below"
              : "Fill in the details to create a new task"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium">Title</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="Enter task title" 
                      className="h-10"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium">Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter task description (optional)"
                      rows={3}
                      className="resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium">Note</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter additional notes or detailed instructions (optional)"
                      rows={4}
                      className="resize-none"
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Additional information or detailed instructions for this task
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-6 sm:grid-cols-1">
                <div className="grid gap-6 sm:grid-cols-3 rounded-lg border border-border bg-muted/20 p-4">
            <FormField
                  control={form.control}
                  name="recurrence_pattern"
                  render={({ field }) => (
                    <FormItem className="grid">
                      <FormLabel className="text-sm font-medium">Pattern</FormLabel>
                      <FormControl>
                        <Combobox
                          value={field.value || ""}
                          onValueChange={field.onChange}
                          options={recurrenceOptions}
                          placeholder="Select pattern"
                          dwimOnChange
                          isModal
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                      </FormDescription>

                      <FormMessage />
                    </FormItem>
                  )}
                />

              <FormField
                  control={form.control}
                  name="recurrence_calculation_type"
                  render={({ field }) => (
                    <FormItem className="space-y-2 grid">
                      <FormLabel className="text-sm font-medium">Calculation Type</FormLabel>
                      <FormControl>
                        <Combobox
                          value={field.value || ""}
                          onValueChange={field.onChange}
                          options={calculationTypeOptions}
                          placeholder="Select calculation type"
                          dwimOnChange
                          isModal
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        <ul>
                          <li>Fixed: Based on <b>original due date</b></li>
                          <li>From Completion: Based on <b>date completed</b></li>
                        </ul>
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recurrence_interval"
                  render={({ field }) => (
                    <FormItem className="space-y-2 grid">
                      <FormLabel className="text-sm font-medium">Interval</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min="1"
                          className="h-10"
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 1)
                          }
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        How often the task recurs
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

            {isEdit && (
                <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border bg-muted/10 p-4">
                    <div className="space-y-1">
                      <FormLabel className="text-sm font-medium cursor-pointer">Active Status</FormLabel>
                      <FormDescription className="text-xs">
                        Enable or disable this task
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTask.isLoading || updateTask.isLoading}
              >
                {createTask.isLoading || updateTask.isLoading
                  ? "Saving..."
                  : isEdit
                  ? "Update Task"
                  : "Create Task"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
